import React, { FC, ReactElement, memo, useCallback, useEffect, useState } from 'react';
import { Form } from 'antd';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { Modal } from 'shared/ui/Modal';
import { TextArea } from 'shared/ui/TextArea';
import { WarningPing } from 'shared/ui/WarningPing';
import { showNotification } from 'app/providers/NotificationsProvider';
import { CreditPaymentParams, useCreditPaymentMutation } from 'entities/Payment';
import { InputNumber } from 'shared/ui/InputNumber';
import { UserPermissions } from 'entities/User';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { WarnTag } from 'shared/ui/WarnTag';
import { Select } from 'shared/ui/Select';
import { PaymentCreditNoteType } from 'entities/CreditNote/model/types';

interface CreditPaymentProps {
  paymentId: string;
  amount: number;
  isClosedContract: boolean;
  action: ReactElement;
}

export const CreditPayment: FC<CreditPaymentProps> = memo((props) => {
  const { paymentId, amount, isClosedContract, action } = props;

  const [isOpenedModal, setOpenedModal] = useState(false);

  const isForbidden = useCheckPermission(UserPermissions.CREDIT_PAYMENT);

  const [form] = Form.useForm();

  const { t } = useAppTranslation(['contracts', 'common']);

  const [creditPayment, { isLoading }] = useCreditPaymentMutation();

  const creditNoteTypeOptions = [
    { label: t(PaymentCreditNoteType.PAYMENT_CREDIT, { ns: 'common' }), value: PaymentCreditNoteType.PAYMENT_CREDIT },
    {
      label: t(PaymentCreditNoteType.PAYMENT_REVERSAL_CREDIT, { ns: 'common' }),
      value: PaymentCreditNoteType.PAYMENT_REVERSAL_CREDIT,
    },
  ];

  const openModal = useCallback((): void => {
    form.setFieldValue('creditNoteType', PaymentCreditNoteType.PAYMENT_CREDIT);
    setOpenedModal(true);
  }, [form]);

  const closeModal = useCallback((): void => {
    setOpenedModal(false);
  }, []);

  const handleCreditPayment = useCallback(
    async (data: CreditPaymentParams): Promise<void> => {
      try {
        if (paymentId) {
          await creditPayment({ ...data, paymentId }).unwrap();
          showNotification('info', t('Success', { ns: 'common' }), t('Payment has been successfully cancelled'));
        }
      } catch (error: CustomAny) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when cancelling payment'));
      } finally {
        closeModal();
      }
    },
    [paymentId, creditPayment, t, closeModal],
  );

  useEffect(() => {
    form.setFieldValue('creditedAmount', amount);
  }, [amount, form]);

  return (
    <>
      {React.cloneElement(action, { onClick: openModal })}

      <Modal className="max-w-[400px]" isOpen={isOpenedModal} onClose={closeModal}>
        <Form form={form} layout="vertical" onFinish={handleCreditPayment} className="flex flex-col" disabled={isForbidden}>
          <div className="mb-2">
            <WarningPing type="warning" />
          </div>
          <div className="font-semibold text-xl mb-3">{t('Cancel Payment')}</div>

          <div className="text-primaryLight font-normal">{t('Are you sure you want to cancel this payment?')}</div>

          {isClosedContract && (
            <div className="mt-2 mb-2">
              <WarnTag textSize="medium" disableIcon>
                {t('After this payment cancel related invoice will become active')}
              </WarnTag>
            </div>
          )}

          <div className="my-5">
            <Form.Item
              label={t('Enter cancelled amount')}
              name="creditedAmount"
              rules={[
                { required: true, message: `${t('Please, enter cancelled amount')}!` },
                () => ({
                  async validator(_, value) {
                    if (!value || value <= amount) {
                      await Promise.resolve();
                      return;
                    }
                    return await Promise.reject(new Error(`${t('Cancelled amount must be less or equal')} ${amount}!`));
                  },
                }),
              ]}
            >
              <InputNumber placeholder={t('Enter cancelled amount')} bordered isDisabled />
            </Form.Item>

            <Form.Item
              label={t('Credit note type')}
              name="creditNoteType"
              rules={[
                {
                  required: true,
                  message: t('Please select creditNoteType!'),
                },
              ]}
            >
              <Select options={creditNoteTypeOptions} placeholder={t('Please select creditNoteType!')} bordered />
            </Form.Item>

            {/* <Form.Item>
              <Checkbox name="addUserBalance" checked={addUserBalance} onChange={toggleAddUserBalance}>
                <span className="text-m">{t('Add {{amount}} {{currencySymbol}} to balance', { amount, currencySymbol })}</span>
              </Checkbox>
            </Form.Item> */}

            <Form.Item name="description">
              <TextArea placeholder={t('Add your description...')} rows={4} bordered />
            </Form.Item>
          </div>

          <div className="flex justify-end gap-3">
            <Button theme="secondary" onClick={closeModal}>
              {t('Cancel', { ns: 'common' })}
            </Button>

            <Button type="submit" isLoading={isLoading} isDisabled={isForbidden}>
              {t('Save', { ns: 'common' })}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
});
